/* eslint-disable react-refresh/only-export-components */
import React from 'react';
import styled from 'styled-components';
import { Button } from '../ui/Button';

export const AdminToolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
  margin-bottom: 0.75rem;
`;

export const AdminTableScroll = styled.div`
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

export const AdminTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 1rem;
`;

export const AdminTh = styled.th`
  text-align: left;
  padding: 0.85rem 1rem;
  background-color: #f8f9fa;
  border-bottom: 2px solid #e9ecef;
  white-space: nowrap;
`;

export const AdminTd = styled.td`
  padding: 0.85rem 1rem;
  border-bottom: 1px solid #e9ecef;
  vertical-align: middle;
`;

export const AdminModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: ${({ theme }) => theme.zIndices.modalBackdrop};
  padding: 1rem;
`;

export const AdminModalContent = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 8px;
  width: 100%;
  max-width: 520px;
  max-height: 90vh;
  overflow-y: auto;
`;

export const FieldLabel = styled.label`
  display: block;
  font-weight: 600;
  font-size: 0.9rem;
  margin-bottom: 0.35rem;
  color: #374151;
`;

export const FilterInput = styled.input`
  padding: 0.45rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.9rem;
  min-width: 200px;
  &:focus {
    outline: none;
    border-color: #6366f1;
    box-shadow: 0 0 0 2px #e0e7ff;
  }
`;

export const FilterSelect = styled.select`
  padding: 0.45rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.9rem;
  background: #fff;
  cursor: pointer;
  &:focus {
    outline: none;
    border-color: #6366f1;
  }
`;

export const FormDialog = ({
  title,
  children,
  onCancel,
  onSubmit,
  submitLabel = 'Opslaan',
}: {
  title: string;
  children: React.ReactNode;
  onCancel: () => void;
  onSubmit: () => void;
  submitLabel?: string;
}) => (
  <AdminModalOverlay onClick={onCancel}>
    <AdminModalContent onClick={(event) => event.stopPropagation()}>
      <h3 style={{ marginBottom: '1rem' }}>{title}</h3>
      {children}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', marginTop: '1.5rem' }}>
        <Button variant="ghost" onClick={onCancel}>Annuleer</Button>
        <Button onClick={onSubmit}>{submitLabel}</Button>
      </div>
    </AdminModalContent>
  </AdminModalOverlay>
);

export const FileUploadField = ({
  label,
  accept,
  onChange,
}: {
  label: string;
  accept?: string;
  onChange: (file: File | null) => void;
}) => (
  <div>
    <FieldLabel>{label}</FieldLabel>
    <input type="file" accept={accept} onChange={(event) => onChange(event.target.files?.[0] ?? null)} />
  </div>
);

export const toCsv = (rows: Array<Record<string, string | number | boolean | null | undefined>>) => {
  if (rows.length === 0) return '';
  const firstRow = rows[0];
  if (!firstRow) return '';
  const headers = Object.keys(firstRow);
  const escape = (value: unknown) => `"${String(value ?? '').replace(/"/g, '""')}"`;
  return [headers.join(','), ...rows.map((row) => headers.map((header) => escape(row[header])).join(','))].join('\n');
};

export const toExcelTsv = (rows: Array<Record<string, string | number | boolean | null | undefined>>) => {
  if (rows.length === 0) return '';
  const firstRow = rows[0];
  if (!firstRow) return '';
  const headers = Object.keys(firstRow);
  const escape = (value: unknown) => String(value ?? '').replace(/\t/g, ' ').replace(/\r?\n/g, ' ');
  return [headers.join('\t'), ...rows.map((row) => headers.map((header) => escape(row[header])).join('\t'))].join('\n');
};

export const parseDelimitedRows = (text: string, delimiter = ',') =>
  text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => line.split(delimiter).map((value) => value.trim()));
